import { z } from 'zod';
import { CONSENT_KINDS, HEARD_FROMS, PROVIDER_KINDS, PROVIDER_STATUSES, USER_STATUSES } from './enums.js';
import { IsoDateTime } from './primitives.js';
import { ProviderProfile } from './providers.js';
/** Columns the provider directory can sort by. `submittedAt` puts the review queue oldest first. */
export const ADMIN_PROVIDER_SORTS = ['createdAt', 'submittedAt', 'name'];
export const ADMIN_PROVIDER_PAGE_SIZES = [25, 50, 100];
/**
 * `GET /admin/providers`, and the back office's URL search params for the
 * same list. Every field falls back to its default instead of failing, so an
 * old or hand-edited link still opens a list.
 */
export const AdminProviderListQuery = z.object({
    /** Business name, contact name, email or phone. Words narrow the search. */
    q: z.string().trim().max(100).optional().catch(undefined),
    status: z.enum(PROVIDER_STATUSES).optional().catch(undefined),
    kind: z.enum(PROVIDER_KINDS).optional().catch(undefined),
    sort: z.enum(ADMIN_PROVIDER_SORTS).default('createdAt').catch('createdAt'),
    dir: z.enum(['asc', 'desc']).default('desc').catch('desc'),
    page: z.coerce.number().int().min(1).default(1).catch(1),
    pageSize: z.coerce
        .number()
        .pipe(z.union(ADMIN_PROVIDER_PAGE_SIZES.map((size) => z.literal(size))))
        .default(25)
        .catch(25),
});
/** One row of the provider directory. `phone` is E.164. */
export const AdminProviderRow = z.object({
    id: z.string(),
    legalName: z.string(),
    tradingName: z.string().nullable(),
    kind: z.enum(PROVIDER_KINDS),
    status: z.enum(PROVIDER_STATUSES),
    contactFirstName: z.string(),
    contactLastName: z.string(),
    email: z.string(),
    phone: z.string(),
    /** Where the business is based. Null until the provider fills it in. */
    basedIn: z.object({ name: z.string(), city: z.string() }).nullable(),
    categories: z.array(z.string()),
    serviceAreaCount: z.int(),
    createdAt: IsoDateTime,
    submittedAt: IsoDateTime.nullable(),
});
/** 200 from `GET /admin/providers`: one page of the directory and the total that matched. */
export const AdminProviderList = z.object({
    items: z.array(AdminProviderRow),
    total: z.int(),
    page: z.int(),
    pageSize: z.int(),
});
/** 200 from `GET /admin/providers/{id}`: the provider's profile plus what only the back office sees. */
export const AdminProviderDetail = ProviderProfile.extend({
    userId: z.string(),
    /** The sign-in's status. SUSPENDED means banned. */
    accountStatus: z.enum(USER_STATUSES),
    lastLoginAt: IsoDateTime.nullable(),
    heardFrom: z.enum(HEARD_FROMS),
    /** Internal notes from the last review. Never shown to the provider. */
    reviewNotes: z.string().nullable(),
    reviewedAt: IsoDateTime.nullable(),
    reviewerName: z.string().nullable(),
    liaisonAdminName: z.string().nullable(),
    googlePlaceId: z.string().nullable(),
    /** Every consent decision, newest first: the POPIA record. */
    consents: z.array(z.object({
        kind: z.enum(CONSENT_KINDS),
        version: z.string(),
        granted: z.boolean(),
        at: IsoDateTime,
    })),
});
/**
 * MyHome+ aims to review a submitted provider within this many hours
 * (requirements.md, Provider application; phase-1-scope.md, Admin).
 */
export const PROVIDER_REVIEW_TARGET_HOURS = 48;
/** How many providers the overview lists in its review queue. */
export const ADMIN_OVERVIEW_REVIEW_QUEUE = 5;
/** The overview's providers section. */
export const AdminProvidersSummary = z.object({
    /** Every provider that is not soft deleted. */
    total: z.int(),
    /** Every status, zero included. */
    byStatus: z.record(z.enum(PROVIDER_STATUSES), z.int()),
    /** PENDING_REVIEW providers, longest waiting first. */
    reviewQueue: z.array(AdminProviderRow),
});
//# sourceMappingURL=admin-providers.js.map